import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { FONT_HEAD, FONT_BODY } from './styles'

// Route + admin gate live in App.js (mirrors funnels-admin): render is guarded
// by `&& isAdmin`, and a bounce effect kicks non-admins to /library.
//
// PASS 2: create-team + add/remove-member controls. Agent roster loads from
// profiles and drives every dropdown — showing full_name, greying out anyone
// without an armls_agent_id (that ID is the whole basis for crediting, so it is
// the "can't be picked" rule here — the same self-documenting treatment
// FunnelsAdmin gives brokerage-less agents).
//
// Dropdown scope: agents already on ANY team are filtered out in-memory (clean,
// uses data already loaded) so you never re-pick someone. The DB's unique index
// on team_members.armls_agent_id is still the source of truth — its violation is
// caught and shown as a friendly toast in case of a race.

function isUniqueViolation(err) {
  return !!err && (err.code === '23505' || /duplicate key|unique constraint/i.test(err.message || ''))
}

export default function TeamsAdmin({ session, profile, showToast, navigateTo }) {
  const [loading, setLoading] = useState(true)
  const [teams, setTeams] = useState([])
  const [membersByTeam, setMembersByTeam] = useState({})   // team_id -> [{ id, armls_agent_id }]
  const [agents, setAgents] = useState([])                 // sorted: selectable (has ARMLS ID) first
  const [nameByArmls, setNameByArmls] = useState({})       // armls_agent_id -> full_name

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [teamsRes, agentsRes] = await Promise.all([
        supabase.from('teams').select('id, name, team_lead_id, created_at').order('name'),
        supabase.from('profiles').select('id, full_name, armls_agent_id, role').in('role', ['agent', 'admin']).order('full_name'),
      ])

      if (agentsRes.error) {
        console.error('[agents load]', agentsRes.error)
        showToast('Could not load agents: ' + agentsRes.error.message)
      }
      const rawAgents = agentsRes.data || []
      // Sort: selectable (has ARMLS ID) first, then by name. Mirrors FunnelsAdmin.
      const sortedAgents = [...rawAgents].sort((a, b) => {
        const aHas = !!(a.armls_agent_id && String(a.armls_agent_id).trim())
        const bHas = !!(b.armls_agent_id && String(b.armls_agent_id).trim())
        if (aHas !== bHas) return aHas ? -1 : 1
        return String(a.full_name || '').localeCompare(String(b.full_name || ''))
      })
      const byArmls = {}
      rawAgents.forEach(a => {
        if (a.armls_agent_id) byArmls[a.armls_agent_id] = a.full_name || '(no name)'
      })
      setAgents(sortedAgents)
      setNameByArmls(byArmls)

      if (teamsRes.error) {
        console.error('[teams load]', teamsRes.error)
        showToast('Could not load teams: ' + teamsRes.error.message)
        setTeams([])
        setMembersByTeam({})
        return
      }
      const rows = teamsRes.data || []
      setTeams(rows)

      const ids = rows.map(t => t.id)
      if (ids.length === 0) {
        setMembersByTeam({})
        return
      }
      const membersRes = await supabase
        .from('team_members')
        .select('id, team_id, armls_agent_id')
        .in('team_id', ids)
      if (membersRes.error) {
        console.error('[team_members load]', membersRes.error)
        showToast('Could not load team members: ' + membersRes.error.message)
        setMembersByTeam({})
        return
      }
      const grouped = {}
      ;(membersRes.data || []).forEach(m => {
        if (!grouped[m.team_id]) grouped[m.team_id] = []
        grouped[m.team_id].push({ id: m.id, armls_agent_id: m.armls_agent_id })
      })
      setMembersByTeam(grouped)
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Fire on mount only. Mirrors FunnelsAdmin/OpenHouseAdmin — depending on
  // `load` would re-fire whenever App.js's showToast ref changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  // ── Every ARMLS ID currently on any team (used to filter dropdowns) ──
  const assignedArmls = new Set()
  Object.values(membersByTeam).forEach(list => list.forEach(m => {
    if (m.armls_agent_id) assignedArmls.add(m.armls_agent_id)
  }))

  // ── Create Team ──────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newLead, setNewLead] = useState('')     // lead's armls_agent_id
  const [creating, setCreating] = useState(false)

  function openCreate() {
    setNewName('')
    setNewLead('')
    setCreateOpen(true)
  }
  function closeCreate() {
    setCreateOpen(false)
    setNewName('')
    setNewLead('')
  }

  const nameTrim = newName.trim()
  const createValid = !!nameTrim && !!newLead

  async function createTeam() {
    if (!createValid || creating) return
    setCreating(true)
    try {
      const insertRes = await supabase
        .from('teams')
        .insert({ name: nameTrim, team_lead_id: newLead })
        .select('id')
        .single()
      if (insertRes.error) throw insertRes.error

      // Lead is automatically a member of their own team.
      const memberRes = await supabase
        .from('team_members')
        .insert({ team_id: insertRes.data.id, armls_agent_id: newLead })
      if (memberRes.error) {
        if (isUniqueViolation(memberRes.error)) {
          showToast('Team created, but the lead is already on another team.')
        } else {
          throw memberRes.error
        }
      } else {
        showToast('Team created')
      }
      closeCreate()
      await load()
    } catch (e) {
      console.error('[team create]', e)
      showToast('Create failed: ' + (e.message || 'unknown'))
    } finally {
      setCreating(false)
    }
  }

  // ── Add member ───────────────────────────────────────────────────────
  const [addSelection, setAddSelection] = useState({})   // team_id -> armls_agent_id
  const [addingTeamId, setAddingTeamId] = useState(null)

  async function addMember(teamId) {
    const armls = addSelection[teamId]
    if (!armls || addingTeamId) return
    setAddingTeamId(teamId)
    try {
      const { error } = await supabase
        .from('team_members')
        .insert({ team_id: teamId, armls_agent_id: armls })
      if (error) {
        if (isUniqueViolation(error)) {
          showToast('That agent is already on a team.')
        } else {
          throw error
        }
      } else {
        showToast('Member added')
        setAddSelection(s => ({ ...s, [teamId]: '' }))
      }
      await load()
    } catch (e) {
      console.error('[member add]', e)
      showToast('Add failed: ' + (e.message || 'unknown'))
    } finally {
      setAddingTeamId(null)
    }
  }

  // ── Remove member ────────────────────────────────────────────────────
  const [removingId, setRemovingId] = useState(null)

  async function removeMember(memberRowId) {
    if (removingId) return
    setRemovingId(memberRowId)
    try {
      const { error } = await supabase.from('team_members').delete().eq('id', memberRowId)
      if (error) throw error
      showToast('Member removed')
      await load()
    } catch (e) {
      console.error('[member remove]', e)
      showToast('Remove failed: ' + (e.message || 'unknown'))
    } finally {
      setRemovingId(null)
    }
  }

  // Agents pickable right now: not already on a team. Disabled (no ARMLS ID)
  // agents stay in the list so the reason they can't be picked is visible.
  const pickableAgents = agents.filter(a => !(a.armls_agent_id && assignedArmls.has(a.armls_agent_id)))

  return (
    <div>
        <div style={{padding:'8px 0 20px'}}>
          <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>Create teams, pick a lead, and add or remove members. Agents without an ARMLS Agent ID can't be added — that ID is how credit is tracked.</div>
        </div>

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',marginBottom:'16px'}}>
          <div style={{fontSize:'12px',color:'#888',fontFamily:FONT_BODY}}>
            {loading ? 'Loading…' : `${teams.length} team${teams.length === 1 ? '' : 's'}`}
          </div>
          {!createOpen && (
            <button
              onClick={openCreate}
              style={{
                padding:'10px 18px',
                borderRadius:'8px',
                border:'none',
                background:'#147efb',
                color:'white',
                fontWeight:800,
                fontFamily:FONT_HEAD,
                fontSize:'13px',
                letterSpacing:'0.5px',
                cursor:'pointer',
              }}
            >+ CREATE TEAM</button>
          )}
        </div>

        {createOpen && (
          <div style={{
            background:'white',
            border:'1px solid #e8ecf1',
            borderRadius:'12px',
            padding:'24px',
            marginBottom:'20px',
          }}>
            <div style={{fontSize:'12px',fontWeight:800,marginBottom:'18px',fontFamily:FONT_HEAD,color:'#1d2e42',letterSpacing:'2px'}}>
              NEW TEAM
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'14px',maxWidth:'560px'}}>
              <Field label="TEAM NAME">
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. North Valley"
                  style={inputStyle}
                />
              </Field>
              <Field label="TEAM LEAD">
                <AgentSelect
                  agents={pickableAgents}
                  value={newLead}
                  onChange={setNewLead}
                  placeholder="— Select lead —"
                />
              </Field>
              <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
                <button
                  onClick={createTeam}
                  disabled={!createValid || creating}
                  style={{
                    padding:'10px 20px',
                    borderRadius:'8px',
                    border:'none',
                    background: (createValid && !creating) ? '#147efb' : '#9fc3ef',
                    color:'white',
                    fontWeight:800,
                    fontFamily:FONT_HEAD,
                    fontSize:'13px',
                    letterSpacing:'0.5px',
                    cursor: (createValid && !creating) ? 'pointer' : 'not-allowed',
                  }}
                >{creating ? 'CREATING…' : 'CREATE'}</button>
                <button
                  onClick={closeCreate}
                  disabled={creating}
                  style={{
                    padding:'10px 20px',
                    borderRadius:'8px',
                    border:'1px solid #d0d7e2',
                    background:'white',
                    color:'#475569',
                    fontWeight:700,
                    fontFamily:FONT_HEAD,
                    fontSize:'13px',
                    letterSpacing:'0.5px',
                    cursor:'pointer',
                  }}
                >CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{padding:'40px 0',color:'#666',fontFamily:FONT_BODY}}>Loading teams…</div>
        ) : teams.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            {teams.map(t => (
              <TeamCard
                key={t.id}
                team={t}
                members={membersByTeam[t.id] || []}
                nameByArmls={nameByArmls}
                pickableAgents={pickableAgents}
                addValue={addSelection[t.id] || ''}
                onAddChange={(v) => setAddSelection(s => ({ ...s, [t.id]: v }))}
                onAdd={() => addMember(t.id)}
                adding={addingTeamId === t.id}
                onRemove={removeMember}
                removingId={removingId}
              />
            ))}
          </div>
        )}
    </div>
  )
}

// Shared dropdown: selectable agents (with ARMLS ID) first and labeled by name;
// agents without an ARMLS ID are disabled with a "needs ARMLS ID" note. Value is
// the agent's armls_agent_id (what team rows store). Mirrors FunnelsAdmin.
function AgentSelect({ agents, value, onChange, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle}>
      <option value="">{placeholder}</option>
      {agents.map(a => {
        const hasId = !!(a.armls_agent_id && String(a.armls_agent_id).trim())
        const baseName = a.full_name || '(no name)'
        const label = hasId ? baseName : `${baseName} — needs ARMLS ID`
        return (
          <option key={a.id} value={hasId ? a.armls_agent_id : ''} disabled={!hasId}>
            {label}
          </option>
        )
      })}
    </select>
  )
}

function Field({ label, hint, error, children }) {
  return (
    <label style={{display:'flex',flexDirection:'column',gap:'5px',fontFamily:FONT_BODY}}>
      <span style={{fontSize:'10px',fontWeight:800,letterSpacing:'1.5px',color:'#475569',fontFamily:FONT_HEAD}}>{label}</span>
      {children}
      {hint && !error && <div style={{fontSize:'11px',color:'#888'}}>{hint}</div>}
      {error && <div style={{fontSize:'12px',color:'#e23744',fontWeight:600}}>{error}</div>}
    </label>
  )
}

const inputStyle = {
  font: 'inherit',
  height: '40px',
  padding: '0 12px',
  border: '1.5px solid #d9e2ec',
  borderRadius: '8px',
  background: 'white',
  color: '#1d2e42',
  boxSizing: 'border-box',
  outline: 'none',
}
const selectStyle = { ...inputStyle, paddingRight: '8px' }

function EmptyState() {
  return (
    <div style={{
      padding:'48px 24px',
      textAlign:'center',
      background:'#f8fafc',
      border:'1px dashed #d0d7e2',
      borderRadius:'12px',
      color:'#475569',
      fontFamily:FONT_BODY,
    }}>
      <div style={{fontSize:'18px',fontWeight:700,color:'#1d2e42',marginBottom:'8px',fontFamily:FONT_HEAD}}>No teams yet</div>
      <div style={{fontSize:'13px'}}>Use “+ CREATE TEAM” above to create the first one.</div>
    </div>
  )
}

function TeamCard({ team, members, nameByArmls, pickableAgents, addValue, onAddChange, onAdd, adding, onRemove, removingId }) {
  const leadName = (team.team_lead_id && nameByArmls[team.team_lead_id]) || team.team_lead_id || '—'
  return (
    <div style={{
      background:'white',
      border:'1px solid #e8ecf1',
      borderRadius:'12px',
      padding:'18px 20px',
      fontFamily:FONT_BODY,
    }}>
      <div style={{fontSize:'18px',fontWeight:800,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginBottom:'6px'}}>
        {team.name || '—'}
      </div>
      <div style={{fontSize:'12px',color:'#475569',marginBottom:'14px'}}>
        Team lead: <span style={{color:'#1d2e42',fontWeight:700}}>{leadName}</span>
      </div>

      <div style={{fontSize:'10px',fontWeight:800,letterSpacing:'1.5px',color:'#64748b',fontFamily:FONT_HEAD,textTransform:'uppercase',marginBottom:'8px'}}>
        Members ({members.length})
      </div>
      {members.length === 0 ? (
        <div style={{fontSize:'13px',color:'#94a3b8',marginBottom:'14px'}}>No members.</div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'14px'}}>
          {members.map(m => {
            const memberName = nameByArmls[m.armls_agent_id] || m.armls_agent_id || '—'
            return (
              <div key={m.id} style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{
                  display:'inline-block',
                  padding:'4px 12px',
                  borderRadius:'999px',
                  fontSize:'13px',
                  background:'rgba(20,126,251,0.08)',
                  color:'#147efb',
                  border:'1px solid rgba(20,126,251,0.25)',
                }}>{memberName}</span>
                <button
                  onClick={() => onRemove(m.id)}
                  disabled={removingId === m.id}
                  style={{
                    padding:'4px 10px',
                    borderRadius:'6px',
                    border:'1px solid #e2b8bc',
                    background:'white',
                    color:'#e23744',
                    fontWeight:700,
                    fontFamily:FONT_HEAD,
                    fontSize:'11px',
                    letterSpacing:'0.5px',
                    cursor: removingId === m.id ? 'wait' : 'pointer',
                    opacity: removingId === m.id ? 0.5 : 1,
                  }}
                >{removingId === m.id ? '…' : 'Remove'}</button>
              </div>
            )
          })}
        </div>
      )}

      <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',borderTop:'1px solid #f1f4f8',paddingTop:'14px'}}>
        <div style={{minWidth:'240px',flex:'1 1 240px'}}>
          <AgentSelect
            agents={pickableAgents}
            value={addValue}
            onChange={onAddChange}
            placeholder="— Add member —"
          />
        </div>
        <button
          onClick={onAdd}
          disabled={!addValue || adding}
          style={{
            padding:'10px 18px',
            borderRadius:'8px',
            border:'none',
            background: (addValue && !adding) ? '#147efb' : '#9fc3ef',
            color:'white',
            fontWeight:800,
            fontFamily:FONT_HEAD,
            fontSize:'13px',
            letterSpacing:'0.5px',
            cursor: (addValue && !adding) ? 'pointer' : 'not-allowed',
          }}
        >{adding ? 'ADDING…' : 'ADD'}</button>
      </div>
    </div>
  )
}
